import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { systemsAPI, reportsAPI, gatewaysAPI, productsAPI } from '../services/api';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { System } from '../types';

const Reports: React.FC = () => {
  const { id } = useParams();
  const [system, setSystem] = useState<System | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [latestValues, setLatestValues] = useState<number[]>([]);
  const [latestCreatedAt, setLatestCreatedAt] = useState<string | undefined>(undefined);
  const [gatewayName, setGatewayName] = useState<string | undefined>(undefined);
  // Multi-series: map paramIndex (1-based) -> series points
  const [seriesMap, setSeriesMap] = useState<Record<number, Array<{ ts: string; value: number }>>>({});
  const [seriesLoading, setSeriesLoading] = useState<boolean>(false);
  // product-level calculation formulas removed; rely on system metadata only
  // Date range (default: first of current month -> today)
  const today = new Date();
  const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
  const defaultFrom = fmtDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const defaultTo = fmtDate(today);
  const [fromDate, setFromDate] = useState<string>(defaultFrom);
  const [toDate, setToDate] = useState<string>(defaultTo);
  // applied date range is used by the data-fetching effect; user edits `fromDate`/`toDate` then clicks Apply
  const [appliedFrom, setAppliedFrom] = useState<string>(defaultFrom);
  const [appliedTo, setAppliedTo] = useState<string>(defaultTo);

  // Fallback: auto-detect parameter indices from latestValues when metadata mappings are missing
  const fallbackPIndices = useMemo(() => {
    const indices: number[] = [];
    latestValues.forEach((v, i) => {
      if (Number.isFinite(v)) indices.push(i + 1); // p1..p50
    });
    return indices;
  }, [latestValues]);

  // Simple IST helpers: today's date string (YYYY-MM-DD) and current IST ISO (+05:30)
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const getIstTodayStr = () => {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
    } catch (e) {
      return fmtDate(new Date(Date.now() + IST_OFFSET_MS));
    }
  };
  const getIstNowIso = () => {
    const d = new Date(Date.now() + IST_OFFSET_MS);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    const ss = String(d.getUTCSeconds()).padStart(2, '0');
    const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${ms}+05:30`;
  };

  useEffect(() => {
    const fetchSystem = async () => {
      if (!id) return;
      setLoading(true);
      setError('');
      try {
        const data = await systemsAPI.getById(id);
        setSystem(data);

        // Resolve gateway name robustly
        let gw: string | undefined = undefined;
        // 1) Populated gateway object
        if (typeof (data as any)?.gatewayId === 'object' && (data as any).gatewayId?.gatewayName) {
          gw = (data as any).gatewayId.gatewayName as string;
        }
        // 2) String id -> fetch gateway
        if (!gw) {
          const gid = typeof (data as any)?.gatewayId === 'string'
            ? (data as any).gatewayId
            : (typeof (data as any)?.gatewayId === 'object'
               ? ((data as any).gatewayId.id || (data as any).gatewayId._id)
               : undefined);
          if (gid) {
            try {
              const g = await gatewaysAPI.getById(gid);
              if (g?.gatewayName) gw = g.gatewayName;
            } catch {
              // ignore
            }
          }
        }
        // 3) Fallback: metadata.gateway from additional products if present
        if (!gw && Array.isArray((data as any)?.additionalProducts)) {
          for (const ap of (data as any).additionalProducts) {
            const metaGw = ap?.metadata?.gateway;
            if (metaGw && typeof metaGw === 'string') { gw = metaGw; break; }
          }
        }

        setGatewayName(gw);
        // no product-level formulas to fetch; system metadata is authoritative
        if (gw) {
          try {
            const last = await reportsAPI.getLatestForGateway(gw);
            const vals: number[] = [];
            for (let i = 1; i <= 50; i++) {
              const v = (last as any)[`p${i}`];
              vals.push(typeof v === 'number' ? v : (v == null ? NaN : Number(v)));
            }
            setLatestValues(vals);
            if ((last as any)?.createdAt) {
              setLatestCreatedAt((last as any).createdAt);
            } else {
              setLatestCreatedAt(undefined);
            }
          } catch {
            // ignore
          }
        } else {
          setLatestValues([]);
          setLatestCreatedAt(undefined);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load system');
      } finally {
        setLoading(false);
      }
    };
    fetchSystem();
  }, [id]);

  // Convenience: reset daterange to current month
  const resetDateRange = () => {
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setAppliedFrom(defaultFrom);
    setAppliedTo(defaultTo);
  };

  const applyDateRange = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  // Highcharts options builder
  const buildChartOptions = (
    seriesDefs: Array<{ name: string; unit?: string; scale?: number; points: Array<{ ts: string | Date; value: number }> }>
  ): Highcharts.Options => {
    return {
      chart: { type: 'line', height: 360, spacing: [10, 10, 10, 10] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        // show full date + time on axis labels and rotate vertically to fit more labels
        labels: {
          rotation: -45,
          align: 'right',
          style: { fontSize: '11px' },
          formatter: function (this: any) {
            // single-line date + time, slightly tilted for readability
            return Highcharts.dateFormat('%Y-%m-%d %H:%M', this.value);
          },
        },
        tickPixelInterval: 90,
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%Y-%m-%d %H:%M',
          hour: '%Y-%m-%d %H:%M',
          day: '%Y-%m-%d',
          week: '%Y-%m-%d',
          month: '%Y-%m',
          year: '%Y'
        }
      },
      yAxis: {
        title: { text: 'Value' },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%Y-%m-%d %H:%M',
        valueDecimals: 3,
        pointFormatter: function (this: any) {
          const unit = (this.series as any).userOptions.__unit as string | undefined;
          const val = typeof this.y === 'number' ? this.y : Number(this.y);
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${val}${unit ? ' ' + unit : ''}</b><br/>`;
        },
      },
      legend: { enabled: true },
      credits: { enabled: false },
      series: seriesDefs.map((sd) => {
        const s = typeof sd.scale === 'number' && isFinite(sd.scale) && sd.scale !== 0 ? sd.scale : 1;
        const data: Array<[number, number]> = sd.points
          .filter(p => Number.isFinite(Number(p.value)))
          .map(p => [new Date(p.ts).getTime() + IST_OFFSET_MS, Number(p.value) / s]);
        const displayName = sd.unit ? `${sd.name} (${sd.unit})` : sd.name;
        return {
          type: 'line',
          name: displayName,
          data,
          lineWidth: 2,
          marker: { enabled: sd.points.length <= 2 },
          __unit: sd.unit,
        } as any;
      }),
    };
  };

  // (Old per-parameter plot handler removed in favor of multi-series rendering)

  const productsList = useMemo(() => {
    type Param = { name?: string; label?: string; unit?: string; dividingFactor?: number; systemType?: string; pIndex?: number };
    type Item = { key: string; title: string; parameters?: Param[]; metaLabel?: string; calculationFormula?: string };
    if (!system) return [] as Item[];
    const list: Item[] = [];
    // Include primary product if metadata parameters present via a synthetic wrapper in additionalProducts[0]? Primary product metadata not stored separately yet; skip unless we later add.
    (system.additionalProducts || []).forEach((ap, idx) => {
      const productName = typeof ap.productId === 'object' && ap.productId
        ? (ap.productId as any).productName
        : 'Product';
      const displayName = ap.metadata?.productNameOverride || ap.metadata?.label || productName;
      const params = ap.metadata?.parameters || [];
      // Only include parameters explicitly marked to show on graph
      const visibleParams = Array.isArray(params)
        ? (params as any[]).filter(p => p && p.showOnGraph === true)
        : [];
      // Determine if a calculation formula exists (populated product, metadata, or fetched)
      const pidRaw = typeof ap.productId === 'object' && ap.productId ? ((ap.productId as any).id || (ap.productId as any)._id || ap.productId) : ap.productId;
      const pid = pidRaw == null ? undefined : String(pidRaw);
      const apCalc = (ap as any).calculationFormula || ap.metadata?.calculationFormula;
      const hasCalculationFormula = Boolean(apCalc);

      console.log(`[Reports] Product ${displayName} (idx ${idx}) visibleParams:`, visibleParams, 'calculationFormula:', apCalc);

      // Include product if it has visible parameters OR a calculation formula (so derived series can be shown)
      if (visibleParams.length > 0 || hasCalculationFormula) {
        list.push({
          key: `additional-${idx}`,
          title: displayName,
          parameters: visibleParams,
          metaLabel: ap.metadata?.label,
            // prefer metadata.calculationFormula (system-level) over populated product.calculationFormula
          calculationFormula: apCalc || ''
        });
      }
    });
    return list;
  }, [system]);

  // Build parameter definitions (flattened) with global param index (1-based)
  const paramDefs = useMemo(() => {
    const seen = new Set<number>();
    const defs: Array<{ pIndex: number; name: string; unit?: string; scale?: number }> = [];
    console.log('[Reports] Building paramDefs from productsList:', productsList);
    productsList.forEach(item => {
      (item.parameters || []).forEach((p: any) => {
        const idx = typeof p.pIndex === 'number' ? p.pIndex : (typeof p.pindex === 'number' ? p.pindex : undefined);
        if (!idx || idx < 1 || idx > 50) return;
        if (seen.has(idx)) return; // keep first occurrence
        seen.add(idx);
        defs.push({
          pIndex: idx,
          name: p.name || `Param ${idx}`,
          unit: p.unit,
          scale: (typeof p.dividingFactor === 'number' && isFinite(p.dividingFactor) && p.dividingFactor !== 0) ? p.dividingFactor : 1,
        });
      });
    });
    return defs;
  }, [productsList]);

  // Helpers to compute derived series from a product calculation formula.
  const extractPIndicesFromFormula = (formula: string): number[] => {
    const set = new Set<number>();
    if (!formula) return [];
    const re = /P(\d{1,2})/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(formula)) !== null) {
      const n = Number(m[1]);
      if (Number.isInteger(n) && n >= 1 && n <= 50) set.add(n);
    }
    return Array.from(set).sort((a, b) => a - b);
  };

  const safeEvaluateExpression = (expr: string): number => {
    // allow digits, whitespace, parentheses and + - * / . characters only
    if (!/^[0-9+\-*/().\s]+$/.test(expr)) return NaN;
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function('return (' + expr + ')');
      const res = fn();
      return typeof res === 'number' && Number.isFinite(res) ? res : NaN;
    } catch (e) {
      return NaN;
    }
  };

  const buildDerivedSeries = (formula: string): { name: string; unit?: string; scale?: number; points: Array<{ ts: string; value: number }> } => {
    const pIndices = extractPIndicesFromFormula(formula);
    if (pIndices.length === 0) return { name: 'Derived', points: [] };

    // collect maps of ts -> value for each referenced pIndex
    const maps: Record<number, Record<string, number>> = {};
    pIndices.forEach(pi => {
      maps[pi] = {};
      const arr = seriesMap[pi] || [];
      arr.forEach(pt => { if (pt && pt.ts) maps[pi][pt.ts] = Number(pt.value); });
    });

    // find intersection of timestamps present in all referenced series
    const tsSets = pIndices.map(pi => new Set(Object.keys(maps[pi])));
    const intersection = tsSets.reduce((acc, s) => acc.filter(k => s.has(k)), Object.keys(maps[pIndices[0]]));

    const points: Array<{ ts: string; value: number }> = [];

    if (intersection.length > 0) {
      intersection.forEach(ts => {
        // build expression by replacing P# with numeric literal
        let expr = formula;
        for (const pi of pIndices) {
          const val = typeof maps[pi][ts] === 'number' ? maps[pi][ts] : NaN;
          expr = expr.replace(new RegExp('P' + pi, 'gi'), '(' + (Number.isFinite(val) ? val : 0) + ')');
        }
        const v = safeEvaluateExpression(expr);
        if (Number.isFinite(v)) points.push({ ts, value: v });
      });
    } else {
      // Fallback: synthesize a single point from latestValues if available
      const latestTs = latestCreatedAt || new Date().toISOString();
      let expr = formula;
      let hasAny = false;
      for (const pi of pIndices) {
        const lv = Number.isFinite(latestValues[pi - 1]) ? latestValues[pi - 1] : 0;
        if (Number.isFinite(lv)) hasAny = true;
        expr = expr.replace(new RegExp('P' + pi, 'gi'), '(' + (Number.isFinite(lv) ? lv : 0) + ')');
      }
      if (hasAny) {
        const v = safeEvaluateExpression(expr);
        if (Number.isFinite(v)) points.push({ ts: latestTs, value: v });
      }
    }

    return { name: 'Derived', points };
  };

  // Export helpers: dynamic import of SheetJS (xlsx) with CSV fallback
  const uniqueSheetName = (existing: Record<string, any>, baseName: string) => {
    const maxLen = 31;
    let name = String(baseName).slice(0, maxLen);
    if (!Object.prototype.hasOwnProperty.call(existing, name)) return name;
    // append numeric suffixes until unique
    for (let i = 1; i < 1000; i++) {
      const suffix = ` (${i})`;
      const candidate = (String(baseName).slice(0, maxLen - suffix.length) + suffix).slice(0, maxLen);
      if (!Object.prototype.hasOwnProperty.call(existing, candidate)) return candidate;
    }
    // fallback: return truncated base
    return name;
  };
  const exportWorkbook = async (sheets: Record<string, Array<{ ts: string; value: number }>>, filename: string) => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      const stringToColor = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const h = Math.abs(hash) % 360;
        const s = 65;
        const l = 50;
        const hslToHex = (h: number, s: number, l: number) => {
          s /= 100; l /= 100;
          const k = (n: number) => (n + h / 30) % 12;
          const a = s * Math.min(l, 1 - l);
          const f = (n: number) => {
            const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
            return Math.round(255 * color).toString(16).padStart(2, '0');
          };
          return `#${f(0)}${f(8)}${f(4)}`;
        };
        return hslToHex(h, s, l);
      };

      for (const [nameRaw, rows] of Object.entries(sheets)) {
        const name = String(nameRaw).slice(0, 31);
        const color = stringToColor(name);
        const data = rows.map(r => {
          const iso = new Date(r.ts).toISOString();
          return { ts: iso, date: iso.slice(0, 10), time: iso.slice(11, 19), value: r.value, color };
        });
        const ws = XLSX.utils.json_to_sheet(data, { header: ['ts', 'date', 'time', 'value', 'color'] });
        XLSX.utils.book_append_sheet(wb, ws, name);
      }

      XLSX.writeFile(wb, filename);
    } catch (e) {
      // fallback: create a combined CSV
      const parts: string[] = [];
      const stringToColor = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const h = Math.abs(hash) % 360;
        const s = 65;
        const l = 50;
        const hslToHex = (h: number, s: number, l: number) => {
          s /= 100; l /= 100;
          const k = (n: number) => (n + h / 30) % 12;
          const a = s * Math.min(l, 1 - l);
          const f = (n: number) => {
            const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
            return Math.round(255 * color).toString(16).padStart(2, '0');
          };
          return `#${f(0)}${f(8)}${f(4)}`;
        };
        return hslToHex(h, s, l);
      };
      for (const [name, rows] of Object.entries(sheets)) {
        const color = stringToColor(name);
        parts.push(`# ${name}`);
        parts.push('ts,date,time,value,color');
        rows.forEach(r => {
          const iso = new Date(r.ts).toISOString();
          parts.push(`${iso},${iso.slice(0,10)},${iso.slice(11,19)},${r.value},${color}`);
        });
        parts.push('');
      }
      const blob = new Blob([parts.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/\.xlsx$/i, '.csv');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  };

  const exportProductSheet = async (title: string, displaySeries: Array<any>) => {
    const sheets: Record<string, Array<{ ts: string; value: number }>> = {};
    displaySeries.forEach((sd) => {
      const rows = (sd.points || []).map((p: any) => ({ ts: p.ts, value: Number(p.value) }));
      const key = uniqueSheetName(sheets, sd.name || 'series');
      sheets[key] = rows;
    });
    const prettyRange = `${appliedFrom || ''}_${appliedTo || ''}`;
    const filename = `${system?.systemCode || 'report'} - ${title} ${prettyRange}.xlsx`;
    await exportWorkbook(sheets, filename);
  };

  const exportAllSheets = async () => {
    const sheets: Record<string, Array<{ ts: string; value: number }>> = {};
    // build sheets from productsList; use product title and each series as separate sheet
    productsList.forEach((item) => {
      // build displaySeries same way as rendering
      const params = item.parameters || [];
      const productFormulaIndices = item.calculationFormula ? extractPIndicesFromFormula(item.calculationFormula) : [];
      let seriesDefs: any[] = [];
        if (item.calculationFormula && typeof item.calculationFormula === 'string' && item.calculationFormula.trim()) {
        const derived = buildDerivedSeries(item.calculationFormula);
        // pick a unit from the product's configured parameters (first non-empty)
        const pickUnit = () => {
          const params = item.parameters || [];
          for (const p of params) {
            if (p && p.unit) return p.unit;
          }
          return undefined;
        };
        const pickLabel = (): string => {
          const params = item.parameters || [];
          for (const p of params) {
            const v = p && (p.label || p.name) ? (p.label || p.name) : undefined;
            if (v) return String(v);
          }
          return `${item.title} (calculated)`;
        };
        seriesDefs = [{ name: pickLabel(), unit: pickUnit() as string | undefined, points: derived.points }];
      } else {
        seriesDefs = (params || []).map((p: any) => {
          const pIndex: number | undefined = typeof p.pIndex === 'number' ? p.pIndex : (typeof p.pindex === 'number' ? p.pindex : undefined);
          if (!pIndex || pIndex < 1 || pIndex > 50) return null;
          if (productFormulaIndices.includes(pIndex)) return null;
          return { name: `${p.name || 'Param'}${p.unit ? ' (' + p.unit + ')' : ''}`, points: seriesMap[pIndex] || [] };
        }).filter(Boolean);
      }
      const useFallback = seriesDefs.length === 0 && fallbackPIndices.length > 0;
      const displaySeries = useFallback
        ? fallbackPIndices.map((idx) => ({ name: `p${idx}`, points: seriesMap[idx] || [] }))
        : seriesDefs;
      displaySeries.forEach((sd) => {
        const raw = `${item.title} - ${sd.name}`;
        const key = uniqueSheetName(sheets, raw);
        sheets[key] = (sd.points || []).map((p: any) => ({ ts: p.ts, value: Number(p.value) }));
      });
    });
    const filename = `${system?.systemCode || 'report'} - all ${appliedFrom || ''}_${appliedTo || ''}.xlsx`;
    await exportWorkbook(sheets, filename);
  };

  // Load series for all configured parameters
  useEffect(() => {
    const run = async () => {
      if (!gatewayName) return;
      // Choose indices to load: configured paramDefs or fallback from latestValues
      // Include p-indices required by calculation formulas in addition to mapped params
      const paramIndices = paramDefs.length > 0 ? paramDefs.map(d => d.pIndex) : [];
      const formulaIndicesSet = new Set<number>();
      (productsList || []).forEach((it: any) => {
        if (it && it.calculationFormula) {
          const idxs = extractPIndicesFromFormula(it.calculationFormula || '');
          idxs.forEach(n => formulaIndicesSet.add(n));
        }
      });
      const formulaIndices = Array.from(formulaIndicesSet);
      // Union of paramIndices and formulaIndices
      let indicesToLoad = Array.from(new Set<number>([...paramIndices, ...formulaIndices]));
      if (indicesToLoad.length === 0) indicesToLoad = fallbackPIndices;
      if (indicesToLoad.length === 0) {
        if (gatewayName && paramDefs.length === 0) {
          // Helpful debug to explain why series isn't being called
          // eslint-disable-next-line no-console
          console.warn('[Reports] No parameter mappings (pIndex) found for gateway:', gatewayName, '— skipping series fetch.');
        }
        return;
      }
      setSeriesLoading(true);
      try {
        const results = await Promise.all(
          indicesToLoad.map(async (pIndex) => {
              try {
              // Send date-only `from` as before; only `to` needs IST instants
              const fromParam = appliedFrom || undefined;
              let toParam: string | undefined = undefined;
              if (appliedTo) {
                if (appliedTo === getIstTodayStr()) {
                  toParam = getIstNowIso();
                } else {
                  toParam = appliedTo + 'T23:59:59.999+05:30';
                }
              }
              // request a very large limit so the backend returns all available points
              const res = await reportsAPI.getSeries(gatewayName, pIndex, 1000000000, fromParam, toParam);
              const raw = res.points as Array<{ ts: string; value: number }>;
              // Client-side guard: ensure points lie within the applied date range (interpret applied dates as IST day boundaries)
              const minMs = appliedFrom ? new Date(appliedFrom + 'T00:00:00+05:30').getTime() : undefined;
              const maxMs = appliedTo ? new Date(appliedTo + 'T23:59:59.999+05:30').getTime() : undefined;
              const cleaned = raw
                .filter(p => Number.isFinite(Number(p.value)) && p.ts)
                .filter(p => {
                      try {
                        const t = new Date(p.ts).getTime();
                        if (minMs != null && t < minMs) return false;
                        if (maxMs != null && t > maxMs) return false;
                        return true;
                      } catch (e) {
                        return false;
                      }
                });
              return { pIndex, points: cleaned };
            } catch {
              return { pIndex, points: [] as Array<{ ts: string; value: number }> };
            }
          })
        );
        const m: Record<number, Array<{ ts: string; value: number }>> = {};
        results.forEach(r => { m[r.pIndex] = r.points; });
        // Debug insight: log counts per pIndex
        // eslint-disable-next-line no-console
        console.debug('[Reports] series counts', Object.fromEntries(results.map(r => [r.pIndex, r.points.length])));
        // If no historical points, synthesize a single point from latestValues so it still shows a dot
        indicesToLoad.forEach((pIndex) => {
          const lv = latestValues[pIndex - 1];
          if ((!m[pIndex] || m[pIndex].length === 0) && Number.isFinite(lv)) {
            const ts = latestCreatedAt || new Date().toISOString();
            // only synthesize fallback point if it falls within the applied date range
            try {
              const tMs = new Date(ts).getTime();
              const minMs = appliedFrom ? new Date(appliedFrom + 'T00:00:00+05:30').getTime() : undefined;
              const maxMs = appliedTo ? new Date(appliedTo + 'T23:59:59.999+05:30').getTime() : undefined;
              if ((minMs == null || tMs >= minMs) && (maxMs == null || tMs <= maxMs)) {
                m[pIndex] = [{ ts, value: lv }];
              }
            } catch (e) {
              // if parsing fails, skip fallback
            }
          }
        });
        setSeriesMap(m);
      } finally {
        setSeriesLoading(false);
      }
    };
    run();
  }, [gatewayName, paramDefs, fallbackPIndices, latestValues, latestCreatedAt, productsList, appliedFrom, appliedTo]);
  // include date range in deps so re-fetch happens when changed

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-3">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <Button as={Link as any} to="/systems" variant="secondary" size="sm">Back</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col xs={12} md={4} className="d-none d-md-block">
          {/* left spacer for larger screens to keep title centered */}
        </Col>
        <Col xs={12} md={4} className="text-center">
          <h3 className="mb-1">System Reports</h3>
          <div className="text-muted small">
            <span className="me-3"><strong>System:</strong> {system?.systemName} <Badge bg="light" text="dark" className="ms-1">{system?.systemCode}</Badge></span>
            <span className="me-3"><strong>Status:</strong> <Badge bg={system?.status === 'online' ? 'success' : system?.status === 'maintenance' ? 'warning' : system?.status === 'error' ? 'danger' : 'secondary'}>{system?.status}</Badge></span>
            <span><strong>Location:</strong> {system?.location || 'N/A'}</span>
          </div>
        </Col>
        <Col xs={12} md={4} className="text-md-end text-center mt-2 mt-md-0">
          <div className="d-flex justify-content-end gap-2">
            <Button as={Link as any} to="/systems" variant="outline-secondary" size="sm">Back to Systems</Button>
          </div>
        </Col>
      </Row>

      {/* Date range inputs (default: 1st of month -> today) */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="d-flex align-items-center">
          <label className="me-2 small mb-0">From</label>
          <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div className="d-flex align-items-center">
          <label className="me-2 small mb-0">To</label>
          <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <div className="ms-2 d-flex" style={{ gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={resetDateRange}>Reset</Button>
          <Button variant="primary" size="sm" onClick={applyDateRange} disabled={fromDate === appliedFrom && toDate === appliedTo}>Apply</Button>
        </div>
      </div>

      {/* Per-product graphs (always render a chart area for each product) */}
      <Row className="g-3">
        {productsList.map((item) => {
          const params = item.parameters || [];
          // Build series for only this product's parameters using explicit pIndex mapping
          let seriesDefs: Array<{ name: string; unit?: string; scale?: number; pIndex?: number; points: Array<{ ts: string; value: number }> }> = [];
          const productFormulaIndices = item.calculationFormula ? extractPIndicesFromFormula(item.calculationFormula) : [];
          if (item.calculationFormula && typeof item.calculationFormula === 'string' && item.calculationFormula.trim()) {
                // Build a derived series from the formula (uses P1..P50 tokens)
                const derived = buildDerivedSeries(item.calculationFormula);
                // pick a unit from this product's parameters
                const pickUnit = () => {
                  const params = item.parameters || [];
                  for (const p of params) {
                    if (p && p.unit) return p.unit;
                  }
                  return undefined;
                };
                // pick a label/name from this product's parameters to use as series name
                const pickLabel = (): string => {
                  const params = item.parameters || [];
                  for (const p of params) {
                    const v = p && (p.label || p.name) ? (p.label || p.name) : undefined;
                    if (v) return String(v);
                  }
                  return `${item.title} (calculated)`;
                };
                seriesDefs = [{ name: pickLabel(), unit: pickUnit() as string | undefined, scale: 1, points: derived.points }];
              } else {
            seriesDefs = params
              .map((p: any) => {
                const pIndex: number | undefined = typeof p.pIndex === 'number' ? p.pIndex : (typeof p.pindex === 'number' ? p.pindex : undefined);
                if (!pIndex || pIndex < 1 || pIndex > 50) return null; // skip unmapped
                // if this product references this pIndex in its calculation formula, skip plotting it separately
                if (productFormulaIndices.includes(pIndex)) return null;
                const scale = (typeof p.dividingFactor === 'number' && isFinite(p.dividingFactor) && p.dividingFactor !== 0) ? p.dividingFactor : 1;
                return {
                  name: `${p.name || 'Param'}${p.unit ? ' (' + p.unit + ')' : ''}`,
                  unit: p.unit,
                  scale,
                  pIndex,
                  points: seriesMap[pIndex] || [],
                };
              })
              .filter(Boolean) as Array<{ name: string; unit?: string; scale?: number; pIndex?: number; points: Array<{ ts: string; value: number }> }>;
          }
          // If no mapped params for this product but we do have fallback indices, show fallback series so user still sees data
          const useFallback = seriesDefs.length === 0 && fallbackPIndices.length > 0;
          const displaySeries = useFallback
            ? fallbackPIndices.map((idx) => ({ name: `p${idx}`, unit: undefined as string | undefined, scale: 1, pIndex: idx, points: seriesMap[idx] || [] }))
            : seriesDefs;
          const anyData = displaySeries.some(s => s.points && s.points.length > 0);
          const options = buildChartOptions(displaySeries);
          // enforce full selected time range on x-axis when available
          try {
            const min = appliedFrom ? (new Date(appliedFrom + 'T00:00:00+05:30').getTime() + IST_OFFSET_MS) : undefined;
            const max = appliedTo ? (new Date(appliedTo + 'T23:59:59.999+05:30').getTime() + IST_OFFSET_MS) : undefined;
            if (options.xAxis && typeof options.xAxis === 'object') {
              (options.xAxis as any).min = min;
              (options.xAxis as any).max = max;
            }
          } catch (e) {
            // ignore
          }
          const seriesKey = displaySeries.map(s => `${s.name}:${s.points?.length || 0}`).join('|');
          return (
            <Col key={item.key} xs={12}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                  <div className="fw-semibold">{item.title}</div>
                  <div className="d-flex align-items-center gap-2">
                    {useFallback && <Badge bg="secondary" title="Fallback data">Fallback</Badge>}
                    {item.metaLabel && <Badge bg="info" title="Label">{item.metaLabel}</Badge>}
                    <Button variant="outline-secondary" size="sm" onClick={async () => { try { await exportProductSheet(item.title, displaySeries); } catch (e) { /* ignore */ } }}>Export</Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {!gatewayName ? (
                    <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: 320, color: '#888' }}>
                      No gateway configured for this system
                    </div>
                  ) : seriesLoading ? (
                    <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: 320 }}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                        <HighchartsReact key={seriesKey} highcharts={Highcharts} options={options} />
                        {!anyData && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#888' }}>
                            No data
                          </div>
                        )}
                        <div className="mt-3 d-flex flex-wrap gap-3">
                          {displaySeries.map((sd: any, si: number) => {
                            const pts = sd.points || [];
                            let latestVal: number | undefined = undefined;
                            if (pts.length > 0) {
                              latestVal = Number(pts[pts.length - 1].value);
                            } else if (sd.pIndex && Number.isFinite(latestValues[sd.pIndex - 1])) {
                              latestVal = latestValues[sd.pIndex - 1];
                            }
                            const val = (typeof latestVal === 'number' && Number.isFinite(latestVal)) ? (sd.scale ? (latestVal / (sd.scale || 1)) : latestVal) : undefined;
                            return (
                              <div key={si} className="small text-muted d-flex align-items-center" style={{ gap: 8 }}>
                                <span className="me-1 text-muted">{sd.name}:</span>
                                <span
                                  className="ms-1 fw-bold text-primary"
                                  style={{ background: 'rgba(13,110,253,0.08)', padding: '2px 8px', borderRadius: 6, display: 'inline-block' }}
                                >
                                  {val == null || !Number.isFinite(val) ? '—' : Number(val).toFixed(3)}{sd.unit ? ' ' + sd.unit : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}

        {productsList.length === 0 && (
          <Col xs={12}>
            <Card className="h-100 shadow-sm">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Auto-detected parameters</div>
                <Badge bg="secondary" title="Fallback">Fallback</Badge>
              </Card.Header>
              <Card.Body>
                {!gatewayName ? (
                  <div className="text-muted">No gateway configured for this system.</div>
                ) : seriesLoading ? (
                  <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: 320 }}>
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : fallbackPIndices.length === 0 ? (
                  <div className="text-muted">No parameters detected from latest values.</div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {(() => {
                      const opts = buildChartOptions(
                        fallbackPIndices.map((idx) => ({
                          name: `p${idx}`,
                          unit: undefined,
                          scale: 1,
                          points: seriesMap[idx] || [],
                        }))
                      );
                      try {
                        const min = appliedFrom ? (new Date(appliedFrom + 'T00:00:00+05:30').getTime() + IST_OFFSET_MS) : undefined;
                        const max = appliedTo ? (new Date(appliedTo + 'T23:59:59.999+05:30').getTime() + IST_OFFSET_MS) : undefined;
                        if (opts.xAxis && typeof opts.xAxis === 'object') {
                          (opts.xAxis as any).min = min;
                          (opts.xAxis as any).max = max;
                        }
                      } catch (e) {
                        // ignore
                      }
                      return <HighchartsReact highcharts={Highcharts} options={opts} />;
                    })()}
                    {fallbackPIndices.every((idx) => !seriesMap[idx] || seriesMap[idx].length === 0) && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#888' }}>
                        No data
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default Reports;
